import React, { useCallback, useEffect, useRef, useState } from 'react'
import axios from 'axios'
import Layout from '../../../components/Layout/Layout'
import { toast } from 'react-toastify'
import { useNavigate } from 'react-router-dom'

const AdminConferenceStore = () => {
  const navigate = useNavigate()

  const [items, setItems] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)
  const [filterQuery, setFilterQuery] = useState('')
  const [rackQuery, setRackQuery] = useState('')
  const [totalRecords, setTotalRecords] = useState(0)
  const [isImporting, setIsImporting] = useState(false)
  const fileInputRef = useRef(null)

  useEffect(() => {
    const shouldFetch = filterQuery.length > 3 || filterQuery === ''
    if (shouldFetch) {
      fetchData(page)
    }
  }, [page, filterQuery, rackQuery])

  useEffect(() => {
    if (filterQuery.length > 3 || filterQuery === '') {
      setPage(1)
    }
  }, [filterQuery])

  // Reset to the first page when the rack filter changes, otherwise the next
  // scroll would jump into the middle of a different result set.
  useEffect(() => {
    setPage(1)
  }, [rackQuery])

  const fetchData = async (newPage = 1, showToast = false) => {
    setIsLoading(true)
    try {
      const response = await axios.get(
        '/api/v1/admin/store/get-conference-data',
        {
          params: {
            page: newPage,
            limit: 30,
            search: filterQuery,
            rack: rackQuery,
          },
        }
      )
      const { data, total, totalPages } = response.data
      setTotalRecords(total ?? 0)
      if (newPage === 1) {
        setItems(data)
      } else {
        setItems((prev) => [...prev, ...data])
      }
      setHasMore(newPage < totalPages)
      if (showToast) {
        toast.success('Data is up to date')
      }
    } catch (error) {
      console.error('Failed to fetch data:', error)
      setItems([])
    } finally {
      setIsLoading(false)
    }
  }

  const handleImportFile = async (event) => {
    const file = event.target.files?.[0]
    // Let the same file be chosen again after a failed attempt.
    event.target.value = ''
    if (!file) return

    const confirmed = window.confirm('All data will be replaced. Continue?')
    if (!confirmed) return

    const formData = new FormData()
    formData.append('file', file)

    setIsImporting(true)
    try {
      const { data } = await axios.post(
        '/api/v1/admin/store/import/conference-store',
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 }
      )
      toast.success(
        `Imported ${data.importedCount} rows, replaced ${data.removedCount}`
      )
      setPage(1)
      await fetchData(1)
    } catch (error) {
      console.error('Import failed:', error)
      toast.error(error.response?.data?.message || 'Failed to import the file.')
    } finally {
      setIsImporting(false)
    }
  }

  // Same truncation the check list tables use, so long titles do not stretch
  // the column.
  const truncateWords = (text, limit = 8) => {
    if (!text) return ''
    const words = text.split(' ')
    return words.length > limit ? words.slice(0, limit).join(' ') + '...' : text
  }

  const handleFilterChange = (value) => {
    setFilterQuery(value)
  }

  const observer = useRef()
  const lastElementRef = useCallback(
    (node) => {
      if (isLoading) return
      if (observer.current) observer.current.disconnect()
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setPage((prevPage) => prevPage + 1)
        }
      })
      if (node) observer.current.observe(node)
    },
    [isLoading, hasMore]
  )

  return (
    <Layout title='MW-Conference'>
      {/* Blocks the page while the replace runs - leaving the old rows clickable
          would invite edits against data that is being thrown away. */}
      {isImporting && (
        <div
          className='d-flex flex-column align-items-center justify-content-center'
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.75)',
            zIndex: 1100,
          }}
        >
          <div className='spinner-border mb-3' role='status'>
            <span className='visually-hidden'>Importing…</span>
          </div>
          <div className='fw-bold'>Importing, please wait…</div>
        </div>
      )}

      <div
        className='shadow-sm p-3 bg-white rounded'
        style={{ width: '100%', position: 'fixed', height: '65px' }}
      >
        <div className='d-flex align-items-center flex-wrap gap-3'>
          {/* Title and count on the left, so the row reads left to right. */}
          <div className='fw-bold lh-1' style={{ fontSize: '18px' }}>
            MW-Conference
            <span
              className='text-muted fw-normal ms-2'
              style={{ fontSize: '14px' }}
            >
              {/* Reflects the current search, not just the whole store. */}
              {totalRecords.toLocaleString()} records
            </span>
          </div>

          <div className='d-flex ms-auto gap-3'>
            {/* Rack filter - partial and case-insensitive, so a1 finds A1.
                Same 110px box as the stock check lists. */}
            <div className='d-flex'>
              <input
                type='text'
                className='form-control'
                style={{ width: '110px' }}
                placeholder='Rack'
                onChange={(e) => setRackQuery(e.target.value)}
                value={rackQuery}
              />
              <button
                className='btn btn-secondary'
                onClick={() => setRackQuery('')}
                disabled={!rackQuery}
              >
                Clear
              </button>
            </div>

            <div className='d-flex'>
              <input
                type='text'
                className='form-control'
                style={{ width: '250px', maxWidth: '100%' }}
                placeholder='Search Item Code or Desc'
                onChange={(e) => handleFilterChange(e.target.value)}
                value={filterQuery}
              />
              <button
                className='btn btn-secondary'
                onClick={() => setFilterQuery('')}
                disabled={!filterQuery}
              >
                Clear
              </button>
            </div>

            <input
              type='file'
              accept='.xls,.xlsx'
              ref={fileInputRef}
              onChange={handleImportFile}
              style={{ display: 'none' }}
            />
            <button
              type='button'
              className='btn btn-success'
              onClick={() => fileInputRef.current?.click()}
              disabled={isImporting}
            >
              {isImporting ? 'Importing...' : 'Import'}
            </button>

            <button
              type='button'
              className='btn btn-secondary'
              onClick={() => navigate('/admin/store')}
            >
              Back
            </button>
          </div>
        </div>
      </div>

      <div className='table-responsive'>
        <table className='table actable' style={{ marginTop: '70px' }}>
          <thead>
            <tr>
              <th>Rack/Box</th>
              <th>Item Code/Desc</th>
              <th className='text-center'>Qty</th>
              <th className='text-center'>UOMRate</th>
              <th className='text-center'>Total</th>
              <th>Info</th>
            </tr>
          </thead>
          <tbody>
            {/* While the first page is in flight the table is empty, so the
                spinner stands in for the rows rather than "No records". */}
            {items.length === 0 && isLoading ? (
              <tr>
                <td colSpan='6' className='text-center py-4'>
                  <div className='spinner-border spinner-border-sm me-2' />
                  Loading...
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan='6' className='text-center'>
                  No records
                </td>
              </tr>
            ) : (
              items.map((item) => (
                <tr key={item._id}>
                  <td>
                    {item.rackNo}
                    {item.boxNo != null && item.boxNo !== '' && (
                      <div>{item.boxNo}</div>
                    )}
                  </td>

                  <td>
                    {item.itemCode}
                    {item.description && (
                      <div title={item.description}>
                        {truncateWords(item.description)}
                      </div>
                    )}
                  </td>
                  <td className='text-center'>{item.quantity}</td>
                  <td className='text-center'>{item.uomRate}</td>
                  <td className='text-center'>
                    {item.quantity * item.uomRate}
                  </td>
                  <td>{item.remark}</td>
                </tr>
              ))
            )}

            {/* Paging spinner, centred under the rows already on screen. */}
            {items.length > 0 && isLoading && (
              <tr>
                <td colSpan='6' className='text-center py-3'>
                  <div className='spinner-border spinner-border-sm me-2' />
                  Loading...
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {/* Scroll sentinel - kept outside the table so a row is never the
          observer target. */}
      <div ref={lastElementRef} style={{ height: '1px' }} />
    </Layout>
  )
}

export default AdminConferenceStore
